import classNames from 'classnames';
import { ComponentProps, MouseEventHandler, ReactNode } from 'react';
import type Typography from '@alltrails/core/components/Typography';
import ChevronSm from '../../icons/ChevronSm';
import { InputBaseProps, Size } from '../../types';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import IconRenderer, { IconDefinition } from '../IconRenderer';
import InputField from '../InputField';
import styles from './styles/styles.module.scss';

export type SelectProps = InputBaseProps & {
  className?: string;
  labelTextColor?: ComponentProps<typeof Typography>['color'];
  hasError?: boolean; // Used to display error styling when the error text is displayed outside of the component
  leadingIcon?: IconDefinition<'color' | 'orientation'>;
  onChange: (value: string) => void;
  onClick?: MouseEventHandler<HTMLSelectElement>;
  options: { label: ReactNode; value: string }[];
  placeholder: string;
  required?: boolean;
  size?: Size<'sm' | 'md'>;
  testId?: string;
  value?: string;
};

const Select = ({
  className,
  labelTextColor,
  disabled,
  errorText,
  hasError,
  helperText,
  hideLabel,
  labelText,
  labelTextVariant,
  leadingIcon,
  onChange,
  onClick,
  options,
  placeholder,
  required = false,
  size = 'md',
  testId,
  value
}: SelectProps) => {
  return (
    <InputField
      {...setDenaliAttribute('Select')}
      className={className}
      labelTextColor={labelTextColor}
      disabled={disabled}
      errorText={errorText}
      helperText={helperText}
      labelText={hideLabel ? '' : labelText}
      labelTextVariant={labelTextVariant}
      size={size}
    >
      <div className={styles.selectContainer}>
        {leadingIcon && (
          <div className={classNames(styles.icon, styles[size])}>
            <IconRenderer icon={leadingIcon} defaults={{ color: '--color-text-primary', orientation: 'up', size }} />
          </div>
        )}
        <select
          aria-label={hideLabel ? labelText : undefined}
          className={classNames(styles.select, styles[size], {
            [styles.placeholder]: !value,
            [styles.hasError]: hasError || errorText,
            [styles.hasLeadingIcon]: !!leadingIcon
          })}
          disabled={disabled}
          onChange={e => {
            onChange(e.target.value);
          }}
          onClick={onClick}
          required={required}
          value={value}
          data-testid={testId}
        >
          {placeholder && (
            <option key="select-placeholder" value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <div className={classNames(styles.chevron, styles[size])}>
          <ChevronSm size={size} color={disabled ? '--color-text-disabled' : '--color-text-secondary'} orientation="down" />
        </div>
      </div>
    </InputField>
  );
};

export default Select;
